import React, { useState, useImperativeHandle, forwardRef } from 'react';
import toast from 'react-hot-toast';
import Routes from '../../routes';
import {
    DateInput,
    Select,
    Input,
    CommonModal,
    PrimaryButton,
    OnOffSwitch,
    InfoSwitch
} from '../Common';
import { createYearValues, postRequestHeaders } from '../../utils';
import { Grid, Divider } from '@mui/material';
import Enums from '../../enums';

const { LocationType } = Enums;

const OFFSITE_TOOLTIP = "When enabled, this location is treated as an outside facility, such as a supplier, reclaimer or service company. Required for Destructor locations.";
const SUPPLIER_TOOLTIP = "When enabled, this location appears as an option in Acquired From when adding an asset, and is shown as the Vendor/Supplier on assets moved from it.";
const DESTRUCTOR_TOOLTIP = "When enabled, this location is a destination in Send Cylinders to Recycling and can't be used in other cylinder moves. Is Offsite must also be enabled.";
const OFFSITE_HELPER = "Turn on Is Offsite to add this location as a destination in Send Cylinders to Recycling. Until then, it can't be used in any cylinder move.";
const SUPPLIER_HELPER = "To add this location as an option in Acquired From, turn on Is Supplier.";
const DESTRUCTOR_HELPER = "To add this location as a destination in Send Cylinders to Recycling, turn on Is Destructor and Is Offsite.";

const LocationDetails = forwardRef(({ setActiveStep, codes, data, id }, ref) => {
    const initClassification = data.classification || null;
    const [locationType, setLocationType] = useState(data.locationType);
    const [locationName, setLocationName] = useState(data.locationName);
    const [locationNameError, setLocationNameError] = useState(null);
    const [locationTypeError, setLocationTypeError] = useState(null);
    const [classification, setClassification] = useState(initClassification);
    const [classificationAdd, setClassificationAdd] = useState(false);
    const [primaryUsageType, setPrimaryUsageType] = useState(data.primaryUsageType);
    const [naicsBusinessTypeCode, setNaicsBusinessTypeCode] = useState(data.naicsBusinessTypeCode);
    const [sicCode, setSicCode] = useState(data.sicCode);
    const [aqmdId, setAqmdId] = useState(data.aqmdId);
    const [epaId, setEpaId] = useState(data.epaId);
    const [refId, setRefId] = useState(data.refId);
    const [taxId, setTaxId] = useState(data.taxId);
    const [sqft, setSqft] = useState(data.sqft);
    const [occupancy, setOccupancy] = useState(data.occupancy);
    const [constructionStatus, setConstructionStatus] = useState(data.constructionStatus);
    const [code, setCode] = useState(data.code);
    const [temporaryBuilding, setTemporaryBuilding] = useState(data.temporaryBuilding);
    const [notes, setNotes] = useState(data.notes);
    const [federalProperty, setFederalProperty] = useState(data.federalProperty);
    const [operationDate, setOperationDate] = useState(data.operationDate);
    const [operationEndDate,setOperationEndDate] = useState(data.operationEndDate)
    const [yearBuilt, setYearBuilt] = useState(data.yearBuilt);
    const [instanceAssociationId, setInstanceAssociationId] = useState(data.instanceAssociationId);
    const [instanceAssociationAdd, setInstanceAssociationAdd] = useState(false);
    const [preferredVendorId, setPreferredVendorId] = useState(data.preferredVendorId === 0 ? null : data.preferredVendorId);
    const [isOffsite, setIsOffsite] = useState(data.isOffsite);
    const [isSupplier, setIsSupplier] = useState(!!data.isSupplier);
    const [isDestructor, setIsDestructor] = useState(!!data.isDestructor);
    const [newAssociationName, setNewAssociationName] = useState("");
    const [newAssociationNameError, setNewAssociationNameError] = useState(null);
    const [newClassification, setNewClassification] = useState("");
    const [newClassificationError, setnewClassificationError] = useState(null);
    const [locationRegistration, setLocationRegistration] = useState(null);
    const [operationDateError, setOperationDateError] = useState(null);
    const [operationEndDateError, setOperationEndDateError] = useState(null);
    const years = createYearValues(new Date().getFullYear(), 100);
    const isWholesalerSupplier = Number(locationType) === LocationType.WholesalerSupplier;
    // Responsive: xs follows the CreateWorkOrder rhythm (no extra top margin),
    // md keeps the historical 2px. Only valid inside sx (not inline style).
    const gridContainerStyle = { marginTop: { xs: 0, md: "2px" } };
    
    const validateDate = (inputDate, allowFuture = false) => {
        if (!inputDate) return null;

        const date = new Date(inputDate).setHours(0, 0, 0, 0);
        const today = new Date().setHours(0, 0, 0, 0); 

        if (allowFuture && date < today) return "Date cannot be in the past";
        if (!allowFuture && date > today) return "Date cannot be in the future";

        return null;
    };

    useImperativeHandle(ref, () => ({

        validate() {

            var foundError = false;

            if (!locationName || locationName === '') {
                setLocationNameError("This field is required");
                setActiveStep(0);
                foundError = true;
            } else {
                setLocationNameError(null);
            }


            if (!locationType || locationType === '') {
                setLocationTypeError("This field is required");
                setActiveStep(0);
                foundError = true;
            } else {
                setLocationTypeError(null);
            }

            const operationError = validateDate(operationDate);
            if (operationError) {
                setOperationDateError(operationError);
                setActiveStep(0);
                foundError = true;
            } else {
                setOperationDateError(null);
            }
            
            const operationEndDateError = validateDate(operationEndDate);
            if (operationEndDateError) {
                setOperationEndDateError(operationEndDateError);
                setActiveStep(0);
                foundError = true;
            } else {
                setOperationEndDateError(null);
            }

            return !foundError;
        },

        setNameError(message) {
            setLocationNameError(message);
            setActiveStep(0);
        },

        submit() {
            return {
                locationType,
                locationName,
                classification,
                primaryUsageType,
                naicsBusinessTypeCode,
                sicCode,
                aqmdId,
                epaId,
                refId,
                taxId,
                sqft,
                occupancy,
                constructionStatus,
                code,
                temporaryBuilding,
                notes,
                federalProperty,
                operationDate,
                operationEndDate,
                yearBuilt,
                instanceAssociationId,
                preferredVendorId,
                isOffsite,
                isSupplier,
                isDestructor,
                LocationRegistration: locationRegistration
            }
        }

    }));

    const handleLocationTypeChange = (value) => {
        setLocationType(value);

        // Type defaults apply on Create only; Edit keeps the saved flags.
        if (id) return;

        const wholesalerSupplier = Number(value) === LocationType.WholesalerSupplier;
        setIsOffsite(wholesalerSupplier);
        setIsSupplier(wholesalerSupplier);
        setIsDestructor(false);
    };

    const pickClassification = (e) => {
        const pickedClass = codes.instanceLocationClassifications.find(option => option.value === e.target.value);
        if (pickedClass) {
            setClassification(pickedClass.value);
        } else {
            setClassificationAdd(true);
        }
    };

    const addClassificationClick = async () => {
        if (newClassification === "") {
            return setnewClassificationError("This field is required");
        }

        setnewClassificationError(null);

        // AB#16838 (FEXA187): Instance/AddClassification is admin-gated; location editors use
        // the open Location twin so inline classification creation keeps working for non-admins.
        const response = await fetch(Routes.Location.AddClassification(), {
            method: 'POST',
            headers: { ...postRequestHeaders },
            body: JSON.stringify({ name: newClassification })
        });

        if (response.status === 200) {
            const newData = await response.json();
            // US 16874 AC5: an existing name resolves to an id we may already hold; don't re-push it.
            const existing = codes.instanceLocationClassifications.find(c => c.value === newData);
            if (!existing) {
                codes.instanceLocationClassifications.push({ value: newData, name: newClassification });
                toast.success(`The classification has been successfully added`, { duration: 6000 });
            } else {
                toast.success(`"${existing.name}" already exists and has been selected.`, { duration: 6000 });
            }
            setClassification(newData);
        } else {
            toast.error('There was an error saving the new classification', { duration: 5000 });
        }

        setNewClassification("");
        setClassificationAdd(false);
    };

    const addClassificationCancelClick = () => {
        setNewClassification("");
        setClassification(null);
        setnewClassificationError(null);
        setClassificationAdd(false);
    };

    const addInstanceAssociation = async () => {
        setNewAssociationNameError(null);

        if (newAssociationName === "") {
            return setNewAssociationNameError("This field is required");
        }

        const response = await fetch(Routes.Location.AddAssociation(), {
            method: 'POST',
            headers: {...postRequestHeaders},
            body: JSON.stringify({ name: newAssociationName })
        });

        if (response.status === 200) {
            const newData = await response.json();
            // US 16874 AC5: an existing name resolves to an id we may already hold; don't re-push it.
            const existing = codes.instanceAssociations.find(a => a.value === newData);
            if (!existing) {
                codes.instanceAssociations.push({ value: newData, name: newAssociationName });
                toast.success("The Region / Division has been successfully added.", { duration: 5000 });
            } else {
                toast.success(`"${existing.name}" already exists and has been selected.`, { duration: 5000 });
            }
            setInstanceAssociationId(newData);
        } else {
            toast.error("There was an error saving the new association", { duration: 5000 });
        }

        setNewAssociationName("");
        setInstanceAssociationAdd(false);
    };

    const addInstanceAssociationCancel = () => {
        setNewClassification("");
        setInstanceAssociationAdd(false);
    };

    const handleAddInstanceAssociation = (newInstanceAssociationId) => {
        // user has clicked Add new Region / Division
        if (newInstanceAssociationId === 1) {
            setInstanceAssociationId("");
            return setInstanceAssociationAdd(true);
        }

        return setInstanceAssociationId(newInstanceAssociationId);
    };

    return (
        <>
            <Grid container sx={gridContainerStyle} spacing={{ xs: 2, md: 4 }}>
                <Grid item xs={12} md={6} lg={6} style={{ marginBottom: '5px' }}>
                    <Input
                        required
                        fullWidth
                        value={locationName}
                        label="Location Name"
                        onChange={e => setLocationName(e.target.value)}
                        error={locationNameError}
                        helperText={locationNameError}
                    />
                </Grid>
                <Grid item xs={12} md={6} lg={6} style={{ marginBottom: '5px' }}>
                    <Select
                        required
                        label="Location Type"
                        value={locationType}
                        onChange={e => handleLocationTypeChange(e.target.value)}
                        options={codes.locationTypes}
                        error={locationTypeError}
                        width={100}
                    />
                </Grid>
            </Grid>

            <Grid container sx={gridContainerStyle} spacing={{ xs: 2, md: 4 }}>
                <Grid item xs={12} md={6} lg={6} style={{ marginBottom: '5px' }}>
                    <Select
                        label="Region / Division"
                        value={instanceAssociationId || ""}
                        onChange={e => handleAddInstanceAssociation(e.target.value)}
                        options={[
                            { value: 1, name: "Add New Region / Division" },
                            ...codes.instanceAssociations
                        ]}
                    />
                </Grid>
                <Grid item xs={12} md={6} lg={6} style={{ marginBottom: '5px' }}>
                    <Select
                        label="Preferred Vendor"
                        value={preferredVendorId}
                        onChange={e => setPreferredVendorId(e.target.value)}
                        options={codes.preferredVendors}
                        width={100}
                    />
                </Grid>
            </Grid>

            <Grid container sx={gridContainerStyle} spacing={{ xs: 2, md: 4 }}>
                <Grid item xs={12} md={6} lg={6} style={{ marginBottom: '5px' }}>
                    <Select
                        label="Classification"
                        value={classification}
                        customCreateNewItemText="Add New Classifcation"
                        onChange={(e) => pickClassification(e)}
                        options={codes.instanceLocationClassifications}
                        width={100}
                    />
                </Grid>
                <Grid item xs={12} md={6} lg={6} style={{ marginBottom: '5px' }}>
                    <Select
                        label="Usage Type"
                        value={primaryUsageType}
                        onChange={e => setPrimaryUsageType(e.target.value)}
                        options={codes.locationUsageType}
                        width={100}
                    />
                </Grid>
            </Grid>

            <Grid container sx={gridContainerStyle} spacing={{ xs: 2, md: 4 }}>
                <Grid item xs={12} md={6} lg={6} style={{ marginBottom: '5px' }}>
                    <Input

                        fullWidth
                        value={naicsBusinessTypeCode}
                        label="NAICS Business Type Code"
                        onChange={e => setNaicsBusinessTypeCode(e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={6} lg={6} style={{ marginBottom: '5px' }}>
                    <Input
                        fullWidth
                        value={sicCode}
                        label="Standard Industrial Classification (SIC) Code"
                        onChange={e => setSicCode(e.target.value)}
                    />
                </Grid>
            </Grid>

            <Grid container sx={gridContainerStyle} spacing={{ xs: 2, md: 4 }}>
                <Grid item xs={12} md={6} lg={6} style={{ marginBottom: '5px' }}>
                    <Input
                        fullWidth
                        value={aqmdId}
                        label="AQMD ID"
                        onChange={e => setAqmdId(e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={6} lg={6} style={{ marginBottom: '5px' }}>
                    <Input
                        fullWidth
                        value={epaId}
                        label="EPA ID"
                        onChange={e => setEpaId(e.target.value)}
                    />
                </Grid>
            </Grid>

            <Grid container sx={gridContainerStyle} spacing={{ xs: 2, md: 4 }}>
                <Grid item xs={12} md={6} lg={6} style={{ marginBottom: '5px' }}>
                    <Input
                        fullWidth
                        value={refId}
                        label="Ref ID"
                        onChange={e => setRefId(e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={6} lg={6} style={{ marginBottom: '5px' }}>
                    <Input
                        fullWidth
                        value={taxId}
                        label="Operator Federal Tax Identification Number"
                        onChange={e => setTaxId(e.target.value)}
                    />
                </Grid>
            </Grid>

            <Grid container sx={gridContainerStyle} spacing={{ xs: 2, md: 4 }}>
                <Grid item xs={12} md={6} lg={6} style={{ marginBottom: '5px' }}>
                    <Select
                        label="Year Built"
                        value={yearBuilt}
                        onChange={e => setYearBuilt(e.target.value)}
                        options={years}
                        width={100}
                    />
                </Grid>
            </Grid>

            <Grid container sx={gridContainerStyle} spacing={{ xs: 2, md: 4 }}>
                <Grid item xs={12} md={6} lg={6} style={{ marginBottom: '5px' }}>
                    <DateInput
                        required
                        fullWidth
                        date={operationDate}
                        label="Operation Date"
                        onChange={e => setOperationDate(e)}
                        allowPast={true}
                        error={operationDateError}
                        customHelperText={operationDateError}
                    />
                </Grid>
                {id && (
                    <Grid item xs={12} md={6} lg={6} style={{ marginBottom: '5px' }}>
                        <DateInput
                            required
                            fullWidth
                            date={operationEndDate}
                            label="Operation End Date"
                            allowPast={true}
                            allowFuture={false}
                            onChange={e => setOperationEndDate(e)}
                            error={operationEndDateError}
                            customHelperText={operationDateError}
                        />
                    </Grid>
                )}
            </Grid>

            <Grid container sx={gridContainerStyle} spacing={{ xs: 2, md: 4 }}>
                <Grid item xs={12} md={6} lg={6} style={{ marginBottom: '5px' }}>
                    <Input
                        fullWidth
                        value={sqft}
                        label="Gross Area Square Feet"
                        onChange={e => setSqft(e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={6} lg={6} style={{ marginBottom: '5px' }}>
                    <Input
                        fullWidth
                        value={occupancy}
                        label="Occupancy Percent"
                        onChange={e => setOccupancy(e.target.value)}
                    />
                </Grid>
            </Grid>

            <Grid container sx={gridContainerStyle} spacing={{ xs: 2, md: 4 }}>
                <Grid item xs={12} md={6} lg={6} style={{ marginBottom: '5px' }}>
                    <Select
                        label="Construction Status"
                        value={constructionStatus}
                        onChange={e => setConstructionStatus(e.target.value)}
                        options={codes.constructionStatusType}
                    />
                </Grid>
                <Grid item xs={12} md={6} lg={6} style={{ marginBottom: '5px' }}>
                    <Input
                        fullWidth
                        value={code}
                        label="Code"
                        onChange={e => setCode(e.target.value)}
                    />
                </Grid>
            </Grid>

            <Grid container item sx={gridContainerStyle} spacing={{ xs: 2, md: 4 }}>
                <Grid item xs={12} md={6} lg={6} style={{ marginBottom: '5px' }}>
                    <Input
                        fullWidth
                        value={notes}
                        label="Additional Notes"
                        onChange={e => setNotes(e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={6} lg={6}>
                    <OnOffSwitch
                        label='Temporary Building'
                        value={temporaryBuilding}
                        onChange={() => setTemporaryBuilding(!temporaryBuilding)}
                    />
                    <Divider sx={{ marginTop: "2.0%" }} />
                </Grid>
            </Grid>

            <Grid container sx={{ ...gridContainerStyle, marginBottom: '5px' }} spacing={{ xs: 2, md: 4 }}>
                <Grid item xs={12} md={6} lg={6}>
                    <OnOffSwitch
                        label='Federal Property'
                        value={federalProperty}
                        onChange={() => setFederalProperty(!federalProperty)}
                    />
                    <Divider sx={{ marginTop: "2.0%" }} />
                </Grid>
                <Grid item xs={12} md={6} lg={6}>
                    <InfoSwitch
                        label='Is Offsite'
                        tooltip={OFFSITE_TOOLTIP}
                        helperText={isDestructor && !isOffsite ? OFFSITE_HELPER : null}
                        value={isOffsite}
                        onChange={() => setIsOffsite(!isOffsite)}
                    />
                    <Divider sx={{ marginTop: "2.0%" }} />
                </Grid>
            </Grid>

            <Grid container sx={{ ...gridContainerStyle, marginBottom: '5px' }} spacing={{ xs: 2, md: 4 }}>
                <Grid item xs={12} md={6} lg={6}>
                    <InfoSwitch
                        label='Is Supplier'
                        tooltip={SUPPLIER_TOOLTIP}
                        helperText={isWholesalerSupplier && !isSupplier ? SUPPLIER_HELPER : null}
                        value={isSupplier}
                        onChange={() => setIsSupplier(!isSupplier)}
                    />
                    <Divider sx={{ marginTop: "2.0%" }} />
                </Grid>
                <Grid item xs={12} md={6} lg={6}>
                    <InfoSwitch
                        label='Is Destructor'
                        tooltip={DESTRUCTOR_TOOLTIP}
                        helperText={isWholesalerSupplier && !isDestructor ? DESTRUCTOR_HELPER : null}
                        value={isDestructor}
                        onChange={() => setIsDestructor(!isDestructor)}
                    />
                    <Divider sx={{ marginTop: "2.0%" }} />
                </Grid>
            </Grid>

            <CommonModal
                open={instanceAssociationAdd}
                close={addInstanceAssociationCancel}
                header="Add Region/Division"
                headerTopMargin={7}
                width="23%"
            >
                <Grid
                    container
                    spacing={2}
                    justifyContent='center'
                    mt={0}
                >
                    <Grid item xs={10}>
                        <Input
                            required
                            fullWidth
                            label="Region/Division Name"
                            value={newAssociationName}
                            onChange={e => setNewAssociationName(e.target.value)}
                            error={newAssociationNameError}
                            helperText={newAssociationNameError}
                        />
                    </Grid>
                    <Grid item xs={10} mb={2}>
                        <PrimaryButton
                            useLargeButton
                            fullWidth
                            buttonText="Add"
                            onClick={addInstanceAssociation}
                        />
                    </Grid>
                </Grid>
            </CommonModal>

            <CommonModal
                open={classificationAdd}
                close={addClassificationCancelClick}
                header="Add New Classification"
                headerTopMargin={10}
            >
                <Grid
                    container
                    spacing={2}
                    width={450}
                >
                    <Grid item xs={12} align="center" rowSpacing={12}>
                        <Input
                            required
                            sx= {{ width: "84%"}}
                            fullWidth
                            label="Classification Name"
                            value={newClassification}
                            onChange={e => setNewClassification(e.target.value)}
                            error={newClassificationError}
                            helperText={newClassificationError}
                        />
                    </Grid>
                    <Grid item xs={12} align="center" mb={5}>
                        <PrimaryButton
                            useLargeButton
                            width={84}
                            buttonText="Add"
                            onClick={addClassificationClick}
                        />
                    </Grid>
                </Grid>
            </CommonModal>
        </>
    );
});

LocationDetails.displayName = 'LocationDetails';

export default LocationDetails;
